/**
 * Guardado de archivos (Blobs) y otros valores en IndexedDB.
 *
 * Lo usamos como alternativa a localStorage cuando hay que guardar cosas
 * pesadas (imágenes, Blobs, arrays grandes): IndexedDB maneja cientos de MB
 * y guarda datos binarios directos, sin el ~33% extra del base64 ni el tope
 * de ~5MB que revienta la cuota de localStorage.
 *
 * Es genérico: cada área elige su clave (recomendamos prefijo por feature,
 * p. ej. "gantt_captures:12") y, si quiere, un máximo para mantener una
 * lista FIFO bajo esa clave.
 */

const DB_NAME = 'epc_app';
const STORE_NAME = 'files';

// Abrimos (o creamos) la base una sola vez y reutilizamos la promesa.
let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

function tx(mode) {
  return openDb().then(db => db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
}

/**
 * Leemos lo guardado bajo una clave (undefined si no existe).
 * @param {string} key
 * @returns {Promise<*>}
 */
export function getFromIndexedDb(key) {
  return tx('readonly').then(store => new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }));
}

/**
 * Guardamos un valor bajo una clave.
 *
 * Si pasamos maxItems, mantenemos una lista FIFO: metemos el valor al frente
 * y recortamos al máximo. Sin maxItems, guardamos el valor tal cual.
 *
 * @param {string} key
 * @param {*} value
 * @param {number} [maxItems] Máximo de elementos de la lista FIFO (opcional)
 * @returns {Promise<void>}
 */
export function saveToIndexedDb(key, value, maxItems) {
  if (!maxItems) {
    return tx('readwrite').then(store => new Promise((resolve, reject) => {
      const request = store.put(value, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    }));
  }

  return getFromIndexedDb(key).then(existing => {
    let list = Array.isArray(existing) ? existing : [];
    list.unshift(value);
    if (list.length > maxItems) list = list.slice(0, maxItems);

    return tx('readwrite').then(store => new Promise((resolve, reject) => {
      const request = store.put(list, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    }));
  });
}

/**
 * Borramos todas las claves que empiezan por un prefijo (limpieza por feature).
 * @param {string} prefix
 * @returns {Promise<void>}
 */
export function removeFromIndexedDb(prefix) {
  return tx('readwrite').then(store => new Promise((resolve, reject) => {
    const request = store.getAllKeys();
    request.onsuccess = () => {
      (request.result || []).forEach(key => {
        if (typeof key === 'string' && key.indexOf(prefix) === 0) store.delete(key);
      });
      resolve();
    };
    request.onerror = () => reject(request.error);
  })).catch(() => { /* silenciar: si no hay base todavía, no hay nada que borrar */ });
}
